/**
 * The Leadership Brief's readings run: its latest status, and a button that
 * saves this month's readings now.
 *
 * Both go to the project's GUS as the signed-in admin (backend
 * routes/executive_admin.py), and every answer GUS gives is shown as the plain
 * message the backend words for it, in English and Arabic.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { ExecutiveAnswer, ExecutiveReadingsRun, executiveAPI } from '../../services/api';

interface Props {
  projectId: string;
}

const RUN_STATUS: Record<string, { en: string; ar: string }> = {
  ok: { en: 'Finished', ar: 'اكتمل' },
  running: { en: 'Running', ar: 'قيد التشغيل' },
  failed: { en: 'Failed', ar: 'فشل' },
  skipped: { en: 'Skipped', ar: 'تم تخطيه' },
  interrupted: { en: 'Interrupted', ar: 'توقف قبل اكتماله' },
};

/** How each answer that is not "ok" is shown; anything not listed is an error. */
const SEVERITY: Record<string, 'success' | 'info' | 'warning' | 'error'> = {
  started: 'success',
  already_running: 'info',
  rate_limited: 'warning',
  not_built: 'warning',
  not_deployed: 'warning',
  not_signed_in: 'warning',
};

const Bilingual: React.FC<{ en: string; ar: string }> = ({ en, ar }) => (
  <>
    {en}
    {ar && (
      <Box component="span" lang="ar" dir="rtl" sx={{ display: 'block' }}>
        {ar}
      </Box>
    )}
  </>
);

const AnswerAlert: React.FC<{ answer: ExecutiveAnswer<unknown> }> = ({ answer }) => (
  <Alert severity={SEVERITY[answer.outcome] ?? 'error'} sx={{ mb: 1 }}>
    <Bilingual en={answer.message} ar={answer.message_ar} />
    {answer.detail && (
      <Box component="span" sx={{ display: 'block', fontFamily: 'monospace', fontSize: 12, mt: 0.5 }}>
        {answer.detail}
      </Box>
    )}
  </Alert>
);

const Row: React.FC<{ en: string; ar: string; children: React.ReactNode }> = ({ en, ar, children }) => (
  <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 220px) minmax(0, 1fr)', gap: 2, py: 0.5 }}>
    <Typography variant="body2" color="text.secondary">
      {en}{' '}
      <Box component="span" lang="ar" dir="rtl">/ {ar}</Box>
    </Typography>
    <Typography variant="body2" component="div">{children}</Typography>
  </Box>
);

const RunSummary: React.FC<{ run: ExecutiveReadingsRun | null }> = ({ run }) => {
  if (run === null) {
    return (
      <Typography variant="body2" color="text.secondary">
        Never run, or GUS migration 066 is not applied (GUS answers the same way for both). Until a first run
        finishes, the Leadership Brief holds no saved readings: its changes and series read as not yet held, not as
        zero.
        <Box component="span" lang="ar" dir="rtl" sx={{ display: 'block' }}>
          لم يُشغَّل قط، أو لم يُطبَّق ترحيل GUS رقم 066 (يجيب GUS بالطريقة نفسها في الحالتين). إلى أن يكتمل أول تشغيل لا يحتفظ الموجز القيادي بأي قراءات محفوظة: تظهر التغيّرات والسلاسل
          على أنها غير متوفرة بعد، لا على أنها صفر.
        </Box>
      </Typography>
    );
  }
  const status = RUN_STATUS[run.status];
  // No counts (a run that stopped before counting, or one still running) is unknown, not "none failed".
  const failed = run.counts ? run.counts.failed : null;
  return (
    <Box>
      <Row en="Month" ar="الشهر">{run.period}</Row>
      <Row en="Status" ar="الحالة">
        {status ? <Bilingual en={status.en} ar={status.ar} /> : run.status}
      </Row>
      <Row en="Finish time" ar="وقت الانتهاء">
        {run.finished_at ? new Date(run.finished_at).toLocaleString() : '—'}
      </Row>
      <Row en="Measures read" ar="المقاييس المقروءة">
        {run.counts ? run.counts.measures : '—'}
      </Row>
      <Row en="Failed measures" ar="المقاييس التي فشلت">
        {failed === null ? '—' : failed.length === 0 ? (
          <Bilingual en="None" ar="لا شيء" />
        ) : (
          <Box component="ul" sx={{ m: 0, pl: 2 }}>
            {failed.map((id) => (
              <li key={id}><code>{id}</code></li>
            ))}
          </Box>
        )}
      </Row>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
        When the daily check re-reads only the measures an earlier run saved as failed, these counts are that
        re-read&apos;s, not the full run&apos;s.
        <Box component="span" lang="ar" dir="rtl" sx={{ display: 'block' }}>
          عندما يعيد الفحص اليومي قراءة المقاييس التي فشلت في تشغيل سابق فقط، تكون هذه الأعداد لإعادة القراءة تلك،
          لا للتشغيل الكامل.
        </Box>
      </Typography>
    </Box>
  );
};

const LeadershipBriefReadings: React.FC<Props> = ({ projectId }) => {
  const [status, setStatus] = useState<ExecutiveAnswer<{ last_run: ExecutiveReadingsRun | null }> | null>(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [started, setStarted] = useState<ExecutiveAnswer<null> | null>(null);
  const [portalError, setPortalError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setPortalError(null);
    try {
      setStatus(await executiveAPI.readingsStatus(projectId));
    } catch (e: any) {
      setPortalError(e?.response?.data?.detail || e?.message || 'Failed to ask the portal for the readings status');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    load();
  }, [load]);

  const start = async () => {
    setStarting(true);
    setStarted(null);
    setPortalError(null);
    try {
      setStarted(await executiveAPI.startReadings(projectId));
    } catch (e: any) {
      setPortalError(e?.response?.data?.detail || e?.message || 'Failed to ask the portal to start the readings');
    } finally {
      setStarting(false);
    }
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
        <Button variant="contained" size="small" onClick={start} disabled={starting}>
          {starting ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
          Save this month&apos;s readings now
          <Box component="span" lang="ar" dir="rtl" sx={{ ml: 1 }}>/ احفظ قراءات هذا الشهر الآن</Box>
        </Button>
        <Button size="small" startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
          Refresh status / حدّث الحالة
        </Button>
      </Box>

      {portalError && <Alert severity="error" sx={{ mb: 1 }}>{portalError}</Alert>}
      {started && <AnswerAlert answer={started} />}

      {loading ? (
        <CircularProgress size={20} />
      ) : status && status.outcome !== 'ok' ? (
        <AnswerAlert answer={status} />
      ) : status?.body && 'last_run' in status.body ? (
        <RunSummary run={status.body.last_run} />
      ) : status ? (
        <Alert severity="error" sx={{ mb: 1 }}>
          GUS answered without a readings status, so whether a run happened is not known.
          <Box component="span" lang="ar" dir="rtl" sx={{ display: 'block' }}>
            أجاب GUS دون حالة للقراءات، لذا لا يُعرف إن كان قد جرى تشغيل.
          </Box>
        </Alert>
      ) : null}
    </Box>
  );
};

export default LeadershipBriefReadings;
